import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import { AppDialogService } from '@nimmsta/ng-commons-lib/services/app-dialog';
import {
  BooleanIconPipe,
  StatusBadgePipe,
} from '@nimmsta/ng-commons-lib/pipes';
import {
  Column,
  DataTableComponent,
  EmptyTableComponent,
  IconAction,
  NimmstaDataSource,
} from '@nimmsta/ng-commons-lib/data-table';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { WorkflowTemplate } from '@nimmsta/ng-commons-lib/models';
import { ActivateDeactivateAction } from './activateDeactivateAction';
import { WorkflowTemplateDefinitionComponent } from './workflow-template-definition.component';
import { WorkflowTemplateFormComponent } from './workflow-template-form.component';
import { WorkflowTemplateService } from '../../services/workflow-template.service';
import { ShowWorkflowComponent } from '../show-workflow.component';
import { ShowWorkflowIconAction } from '../showWorkflowIconAction';
import { hasWorkflowMediaContent } from '../workflow-media-preview.util';
import { Router } from '@angular/router';

_('workflow.default.title');

@Component({
  selector: 'app-workflow-template',
  templateUrl: './workflow-template.component.html',
  imports: [
    WorkflowTemplateFormComponent,
    TranslatePipe,
    DataTableComponent,
    HeaderBarComponent,
    ErrorComponent,
    AppModelModalComponent,
    EmptyTableComponent,
  ],
  providers: [StatusBadgePipe, BooleanIconPipe],
})
export class WorkflowTemplateComponent implements OnInit {
  private service = inject(WorkflowTemplateService);
  private translate = inject(TranslateService);
  private modalService = inject(AppDialogService);
  private toStatusBadge = inject(StatusBadgePipe);
  private toBooleanIcon = inject(BooleanIconPipe);
  private router = inject(Router);

  source!: NimmstaDataSource<WorkflowTemplate>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<WorkflowTemplate>;

  @ViewChild('EditModal', { static: true })
  private editModalComponent!: AppModelModalComponent<WorkflowTemplate>;

  @ViewChild(WorkflowTemplateFormComponent, { static: true })
  private formComponent!: WorkflowTemplateFormComponent;

  iconActions: IconAction<WorkflowTemplate>[] = [];

  error?: HttpErrorResponse;

  get columns(): Column[] {
    return [
      new Column('name', 'workflow.name', { sortable: true }),
      new Column('description', 'workflow.description'),
      new Column('status', 'workflow.status', { pipe: this.toStatusBadge }),
      new Column('createForOrganization', 'workflow.createForOrganization', {
        pipe: this.toBooleanIcon,
      }),
    ];
  }

  ngOnInit(): void {
    this.source = new NimmstaDataSource<WorkflowTemplate>(this.service);
    const showAction = new ShowWorkflowIconAction<WorkflowTemplate>();
    this.iconActions.push(showAction);
    showAction.subscribe((id) => this.show(id));
    const deActivateAction = new ActivateDeactivateAction(
      '',
      'power_settings_new',
    );
    this.iconActions.push(deActivateAction);
    deActivateAction.subscribe((id) => this.activateDeactivateWorkflow(id));
    const editAction = new IconAction('edit', 'edit');
    this.iconActions.push(editAction);
    editAction.subscribe((id) => this.edit(id));
    const deleteAction = new IconAction('delete', 'delete', true);
    this.iconActions.push(deleteAction);
    deleteAction.subscribe((id) => this.delete(id));
    const definitionAction = new IconAction(
      'workflow.definition',
      'settings_applications',
    );
    this.iconActions.push(definitionAction);
    definitionAction.subscribe((id) => this.showDefinition(id));
    const dashboardAction = new IconAction(
      'dashboard.edit.nav',
      'dashboard_customize',
    );
    this.iconActions.push(dashboardAction);
    dashboardAction.subscribe((id) => this.showDashboard(id));
  }

  new(): void {
    this.translate.get('workflow.default.new').subscribe((value) => {
      const workflow = {} as WorkflowTemplate;
      this.openModal(value, true, workflow);
    });
  }

  show(id: string): void {
    const workflowTemplate = this.getSelectedObject(id)!;
    if (hasWorkflowMediaContent(workflowTemplate)) {
      this.modalService.open(ShowWorkflowComponent, {
        data: {
          workflow: workflowTemplate,
        },
        size: 'xl',
      });
    } else {
      this.edit(id);
    }
  }

  edit(id: string): void {
    this.translate.get('workflow.default.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  openModal(title: string, editable: boolean, value: WorkflowTemplate): void {
    this.formComponent.editable = editable;
    this.formComponent.setValue(value);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show();
  }

  delete(id: string): void {
    this.service.delete(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  onSave(): void {
    this.formComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  getSelectedObject(id: string): WorkflowTemplate | undefined {
    return this.source.data?.content.filter((d) => id === d.id).pop();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }

  showDefinition(id: string) {
    const workflow = this.getSelectedObject(id)!;
    const data = {
      workflow,
    };
    this.modalService.open(WorkflowTemplateDefinitionComponent, {
      data,
      size: 'xl',
      styleClass: 'modal-danger',
    });
  }

  showDashboard(id: string): void {
    this.router.navigate(['/workflow-templates', id, 'dashboard-edit']);
  }

  activateDeactivateWorkflow(id: string) {
    const workflow = this.getSelectedObject(id)!;
    this.service.activateDeactivateWorkflow(workflow).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }
}
